import { Box, forwardRef, Image, StyleProps, useColorModeValue } from '@chakra-ui/react';
import { PropsWithChildren, ReactNode } from 'react';

import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { useSeasonalAvatarOverlay } from '../../../components/seasonal/use-seasonal-avatar-overlay';
import { useNameStyle } from '../hooks/use-name-style';
import { AvatarContextProvider, useAvatarContext } from '../lib/context';
import { AvatarSize, AvatarVariant } from '../types';
import { AvatarImage } from './avatar-image';
import { AvatarImageWrapper } from './avatar-image-wrapper';

export interface AvatarProps extends StyleProps, PropsWithChildren {
	name?: string;
	size?: AvatarSize;
	src?: string;
	variant?: AvatarVariant;
	/**
	 * Optional overlay content rendered on top of the avatar.
	 * Useful for badges, wreaths, status indicators, or other decorations.
	 * The overlay is positioned absolutely and centered on the avatar.
	 *
	 * @example
	 * <Avatar name="John" overlay={<Image src={wreathUrl} width="56px" height="56px" />} />
	 */
	overlay?: ReactNode;
}

export const AvatarSizeMap: Record<AvatarSize, string> = {
	sm: rem(32),
	md: rem(40),
	lg: rem(48),
	xl: rem(56),
	'2xl': rem(72),
};

const useVariantStyle = createUseVariantStyle({
	size: {
		sm: {
			boxSize: AvatarSizeMap.sm,
			fontSize: 'sm',
		},
		md: {
			boxSize: AvatarSizeMap.md,
			fontSize: 'md',
		},
		lg: {
			boxSize: AvatarSizeMap.lg,
			fontSize: 'md',
		},
		xl: {
			boxSize: AvatarSizeMap.xl,
			fontSize: 'lg',
		},
		'2xl': {
			boxSize: AvatarSizeMap['2xl'],
			fontSize: 'xl',
		},
	},
	variant: {
		solid: {},
		outline: {},
	},
});

const AvatarSizeInPx: Record<AvatarSize, number> = {
	sm: 32,
	md: 40,
	lg: 48,
	xl: 56,
	'2xl': 72,
};

/**
 * Inner component that renders the seasonal overlay.
 * Must be rendered inside AvatarContextProvider to access size context.
 */
const SeasonalOverlayRenderer: React.FC<{ overlayImage: string; scale: number }> = ({ overlayImage, scale }) => {
	const { size } = useAvatarContext();
	const overlaySize = `${AvatarSizeInPx[size] * scale}px`;

	return (
		<Box width={overlaySize} height={overlaySize} flexShrink={0}>
			<Image src={overlayImage} alt="" width="100%" height="100%" objectFit="contain" aria-hidden="true" />
		</Box>
	);
};

const Avatar = forwardRef(({ name, overlay, size = 'md', src, variant = 'solid', ...rest }: AvatarProps, ref) => {
	const style = useVariantStyle({ size, variant });
	const { bg, color: textColor } = useNameStyle(name);
	const outlineColor = useColorModeValue('white', 'bg.canvas');

	// Get seasonal overlay configuration (e.g., winter wreath)
	const seasonalOverlay = useSeasonalAvatarOverlay();

	// Determine if we should show an overlay
	// Note: Seasonal overlay only shows when there's an actual image (src), not on fallback/initials avatars
	const hasCustomOverlay = overlay != null;
	const hasSeasonalOverlay = !hasCustomOverlay && seasonalOverlay.isEnabled && Boolean(seasonalOverlay.overlayImage) && Boolean(src);

	// Use CSS Grid to perfectly stack avatar and overlay in the same cell
	// This ensures the overlay is always centered on the avatar regardless of size overrides
	return (
		<AvatarContextProvider value={{ bg, size, textColor, variant }}>
			<Box display="inline-grid" placeItems="center" flexShrink={0} data-avatar-wrapper="" data-avatar-size={size}>
				{/* Grid cell 1,1 - Avatar */}
				<Box
					gridArea="1 / 1"
					alignItems="center"
					as="span"
					bg={variant === 'solid' ? bg : 'white'}
					borderRadius="full"
					overflow="hidden"
					display="inline-flex"
					flexShrink={0}
					justifyContent="center"
					outline="1px solid"
					outlineColor={outlineColor}
					data-testid={`avatar-${name}`}
					ref={ref}
					{...style}
					{...rest}
				>
					<AvatarImageWrapper>
						<AvatarImage name={name} src={src} />
					</AvatarImageWrapper>
				</Box>
				{/* Grid cell 1,1 - Overlay (stacked on top of avatar) */}
				{(hasCustomOverlay || hasSeasonalOverlay) && (
					<Box
						gridArea="1 / 1"
						display="flex"
						alignItems="center"
						justifyContent="center"
						pointerEvents="none"
						zIndex={1}
						data-testid={`avatar-overlay-${name}`}
					>
						{hasCustomOverlay ? (
							overlay
						) : hasSeasonalOverlay && seasonalOverlay.overlayImage ? (
							<SeasonalOverlayRenderer overlayImage={seasonalOverlay.overlayImage} scale={seasonalOverlay.scale} />
						) : null}
					</Box>
				)}
			</Box>
		</AvatarContextProvider>
	);
});

Avatar.displayName = 'Avatar';

export { Avatar };
